import {
  MantineReactTable,
  useMantineReactTable,
  MRT_GlobalFilterTextInput,
  MRT_ShowHideColumnsButton,
} from 'mantine-react-table'
import { ActionIcon as MantineActionButton } from '@mantine/core'
import { ImageIcon, PenIcon, Trash } from 'lucide-react'
import { useResidentialTable } from '../../lib/use-residential-table'
import { FC, ReactNode } from 'react'
import { ResidentialListItem } from '@/shared/api/topli/TopliApi'
import { useTranslation } from 'react-i18next'
import { getLocalization } from '@/widgets/LanguageSelect/lib/consts'

type ResidentialsTableProps = {
  addButtonSlot: ReactNode
  onOpenEditModal: (row: ResidentialListItem) => void
  onOpenDeleteModal: (row: ResidentialListItem) => void
  onOpenFullDescription: (row: ResidentialListItem) => void
  onOpenPhotos: (row: ResidentialListItem) => void
  onOpenMapModal: (row: ResidentialListItem) => void
  onOpenUploadedPhotos: (row: ResidentialListItem) => void
  onOpenVideoModal: (row: ResidentialListItem) => void
}

export const ResidentialsTable: FC<ResidentialsTableProps> = props => {
  const {
    addButtonSlot,
    onOpenEditModal,
    onOpenDeleteModal,
    onOpenFullDescription,
    onOpenPhotos,
    onOpenMapModal,
    onOpenUploadedPhotos,
    onOpenVideoModal,
  } = props
  const {
    pagination,
    columns,
    fetchedResidentials,
    totalRowCount,
    isErrorResidentials,
    isFetchingResidentials,
    isLoadingResidentials,
    sorting,
    setSorting,
    setPagination,
    handleSearchChange,
    columnVisibility,
    setColumnVisibility,
    columnFilters,
    setColumnFilters,
  } = useResidentialTable(onOpenFullDescription, onOpenPhotos, onOpenMapModal, onOpenVideoModal)

  const { t, i18n } = useTranslation('residential')
  const localization = getLocalization(i18n.language)

  const table = useMantineReactTable({
    columns,
    data: fetchedResidentials,
    enableStickyHeader: false,
    enableEditing: false,
    manualPagination: true,
    enableColumnActions: false,
    onPaginationChange: setPagination,
    rowCount: totalRowCount,
    manualSorting: true,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    positionActionsColumn: 'first',
    localization,
    onColumnFiltersChange: setColumnFilters,
    enableColumnFilters: true,
    manualFiltering: true,
    columnFilterDisplayMode: 'popover',
    mantineSearchTextInputProps: {
      classNames: {
        root: 'w-full min-w-[calc(100vw-2rem)] lg:min-w-[16rem]',
        input: 'w-full',
      },
      variant: 'filled',
    },
    enableRowActions: true,
    onGlobalFilterChange: handleSearchChange,
    renderRowActions: ({ row }) => (
      <div className='flex flex-row space-x-1 pr-8'>
        <MantineActionButton
          onClick={() => onOpenEditModal(row.original)}
          className='text-blue bg-blue/10 hover:bg-blue hover:text-white rounded-full transition-all'
          variant='light'
        >
          <PenIcon size={16} />
        </MantineActionButton>
        <MantineActionButton
          onClick={() => onOpenUploadedPhotos(row.original)}
          className='text-blue bg-blue/10 hover:bg-blue hover:text-white rounded-full transition-all'
          variant='light'
        >
          <ImageIcon size={16} />
        </MantineActionButton>
        <MantineActionButton
          onClick={() => onOpenDeleteModal(row.original)}
          className='text-red bg-red/10 hover:bg-red hover:text-white rounded-full transition-all'
          variant='light'
        >
          <Trash size={16} />
        </MantineActionButton>
      </div>
    ),
    renderTopToolbar: ({ table }) => {
      return (
        <div className='flex flex-row flex-wrap lg:flex-nowrap items-center justify-between mb-2 gap-y-4 lg:gap-y-0'>
          <MRT_GlobalFilterTextInput table={table} />
          <div className='flex flex-row items-center space-x-2 w-full lg:w-fit'>
            <MRT_ShowHideColumnsButton className='h-[2.5rem] w-[2.5rem] rounded-md' table={table} />
            {addButtonSlot}
          </div>
        </div>
      )
    },
    mantineToolbarAlertBannerProps: isErrorResidentials
      ? {
          color: 'red',
          children: 'Error loading data',
        }
      : undefined,
    mantinePaperProps: {
      shadow: 'none',
      withBorder: false,
    },
    mantineTableProps: {
      sx: {
        tableLayout: 'auto',
      },
    },
    mantineTableBodyProps: {
      sx: {
        '&: td': {
          minWidth: 90,
        },
        '& tr:hover': {
          backgroundColor: '#FFFFFF',
        },
      },
    },
    mantineTableBodyRowProps: {
      sx: {
        '&:hover': {
          background: '#FAFAFA',
        },
      },
    },
    state: {
      isLoading: isLoadingResidentials,
      pagination,
      sorting,
      showGlobalFilter: true,
      showAlertBanner: isErrorResidentials,
      showProgressBars: isFetchingResidentials,
      columnVisibility,
      columnFilters,
    },
  })

  return <MantineReactTable table={table} />
}
